'use client';

import { c as _c } from "react/compiler-runtime";
import { CompanyResultRow, toCompanyPickerCompany } from '@context/companies';
import type { CommandWorkflowContext } from '@repo/shortcuts/command';
import { useDebouncedValue } from '@repo/tanstack/pacer';
import { CommandGroup, CommandItem } from '@repo/ui/components';
import { AlertCircle, Loader2, Search } from '@repo/ui/icons';
import { useRouter } from 'next/navigation';
import { useCmdkCompanies } from './_hooks/use-cmdk-companies';
import { useCmdkLoads } from './_hooks/use-cmdk-loads';
import { useCmdkUsers } from './_hooks/use-cmdk-users.ts';
import { CmdkSearchHost } from './cmdk-search-host.tsx';
import { LoadResultRow } from './load-result-row.tsx';
import { getCmdkUserTitle, UserResultRow } from './user-result-row.tsx';
const companyGroups = [{
  filter: 'shipper' as const,
  heading: 'Customers',
  includeRisk: true,
  route: 'customers'
}, {
  filter: 'haulier' as const,
  heading: 'Carriers',
  includeRisk: false,
  route: 'carriers'
}, {
  filter: 'broker' as const,
  heading: 'Brokers',
  includeRisk: true,
  route: 'brokers'
}, {
  filter: 'other' as const,
  heading: 'Other companies',
  includeRisk: false,
  route: 'other-companies'
}];
export const GlobalSearchResultsRender = t0 => {
  const $ = _c(50);
  const {
    canListCompaniesByType,
    canListLoads,
    canListUsers,
    context,
    scope: t1
  } = t0;
  const scope = t1 === undefined ? "all" : t1;
  const {
    push
  } = useRouter();
  let t2;
  if ($[0] !== context.search) {
    t2 = context.search.trim();
    $[0] = context.search;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const search = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      wait: 200
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [debouncedSearch] = useDebouncedValue(search, t3);
  const companiesSearchEnabled = canListCompaniesByType && (scope === "all" || scope === "companies") && search.length > 0;
  const query = companiesSearchEnabled ? debouncedSearch : "";
  const {
    data: loadsResponse,
    isLoading: loadsLoading
  } = useCmdkLoads(scope === "all" && canListLoads ? debouncedSearch : "", 5);
  const shipperQuery = useCmdkCompanies("shipper", query, 5);
  const haulierQuery = useCmdkCompanies("haulier", query, 5);
  const brokerQuery = useCmdkCompanies("broker", query, 5);
  const otherQuery = useCmdkCompanies("other", query, 5);
  const companyQueries = {
    broker: brokerQuery,
    haulier: haulierQuery,
    other: otherQuery,
    shipper: shipperQuery
  };
  const {
    data: usersResponse,
    isLoading: usersLoading
  } = useCmdkUsers(scope === "all" && canListUsers ? debouncedSearch : "", 5);
  let t4;
  if ($[3] !== canListLoads || $[4] !== loadsResponse || $[5] !== scope) {
    t4 = scope === "all" && canListLoads ? loadsResponse ?? [] : [];
    $[3] = canListLoads;
    $[4] = loadsResponse;
    $[5] = scope;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const loads = t4;
  let t5;
  if ($[7] !== canListUsers || $[8] !== scope || $[9] !== usersResponse?.data) {
    t5 = scope === "all" && canListUsers ? usersResponse?.data ?? [] : [];
    $[7] = canListUsers;
    $[8] = scope;
    $[9] = usersResponse?.data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const users = t5;
  const companyResults = companiesSearchEnabled ? Object.values(companyQueries).map(_temp) : [];
  const companiesLoading = companiesSearchEnabled && Object.values(companyQueries).some(_temp2);
  const companiesFailed = companiesSearchEnabled && Object.values(companyQueries).some(_temp3);
  let t6;
  if ($[11] !== context.search) {
    t6 = <CmdkSearchHost search={context.search} />;
    $[11] = context.search;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const lifecycleItem = t6;
  if (!search) {
    if (scope === "all") {
      return null;
    }
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <CommandItem disabled={true} forceMount={true} value="__cmdk-instruction-companies__"><Search className="size-4 shrink-0 text-muted-foreground" />Type to search companies</CommandItem>;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] !== lifecycleItem) {
      t8 = <>{lifecycleItem}{t7}</>;
      $[14] = lifecycleItem;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    return t8;
  }
  if (scope === "companies") {
    if (search !== debouncedSearch) {
      let t7;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <CommandItem disabled={true} forceMount={true} value="__cmdk-loading-companies__"><Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />Searching companies...</CommandItem>;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      let t8;
      if ($[17] !== lifecycleItem) {
        t8 = <>{lifecycleItem}{t7}</>;
        $[17] = lifecycleItem;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      return t8;
    }
    if (companyResults.every(_temp4) && !companiesLoading && !companiesFailed) {
      let t7;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <CommandItem disabled={true} forceMount={true} value="__cmdk-empty-companies__"><Search className="size-4 shrink-0 text-muted-foreground" />No companies found.</CommandItem>;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      let t8;
      if ($[20] !== lifecycleItem) {
        t8 = <>{lifecycleItem}{t7}</>;
        $[20] = lifecycleItem;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      return t8;
    }
  }
  if (scope === "all" && (search !== debouncedSearch || loadsLoading || usersLoading)) {
    let t7;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <CommandItem disabled={true} forceMount={true} value="__cmdk-loading-search__"><Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />Searching...</CommandItem>;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== lifecycleItem) {
      t8 = <>{lifecycleItem}{t7}</>;
      $[23] = lifecycleItem;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    return t8;
  }
  const hasResults = loads.length > 0 || users.length > 0 || companyResults.some(_temp5);
  if (!hasResults && !companiesLoading && !companiesFailed) {
    let t7;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <CommandItem disabled={true} forceMount={true} value="__cmdk-empty-search__"><Search className="size-4 shrink-0 text-muted-foreground" />No results found.</CommandItem>;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    let t8;
    if ($[26] !== lifecycleItem) {
      t8 = <>{lifecycleItem}{t7}</>;
      $[26] = lifecycleItem;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    return t8;
  }
  let t7;
  if ($[28] !== context || $[29] !== loads || $[30] !== push || $[31] !== search) {
    t7 = loads.length > 0 && <CommandGroup heading="Loads">{loads.map(load => <CommandItem key={`load-${load.id}`} keywords={[load.id.toString(), load.company?.title ?? "", load.status?.title ?? "", ...load.drops.map(_temp6), search]} onSelect={() => {
        context.close();
        push(`/loads/${load.id}/overview`);
      }} value={`load-${load.id}-${load.company?.title ?? ""}`}><LoadResultRow load={load} /></CommandItem>)}</CommandGroup>;
    $[28] = context;
    $[29] = loads;
    $[30] = push;
    $[31] = search;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  const t9 = companyGroups.map(t8 => {
    const {
      filter,
      heading,
      includeRisk,
      route
    } = t8;
    return (companyQueries[filter].data?.data ?? []).length > 0 && <CommandGroup forceMount={scope === "companies"} heading={heading} key={filter}>{(companyQueries[filter].data?.data ?? []).map(company => <CommandItem forceMount={false} key={`${filter}-${company.id}`} keywords={[company.title, search]} onSelect={() => {
        context.close();
        push(`/management/${route}/${company.id}/overview`);
      }} value={`${filter}-${company.id}-${company.title}`}><CompanyResultRow company={toCompanyPickerCompany(company, {
          includeRisk
        })} /></CommandItem>)}</CommandGroup>;
  });
  let t10;
  if ($[33] !== companiesFailed) {
    t10 = companiesFailed && <CommandItem disabled={true} forceMount={true} value="__cmdk-company-error__"><AlertCircle className="size-4 shrink-0 text-muted-foreground" />Some company results couldn't be loaded.</CommandItem>;
    $[33] = companiesFailed;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== companiesLoading || $[36] !== scope) {
    t11 = companiesLoading && <CommandItem disabled={true} forceMount={true} value={scope === "companies" ? "__cmdk-loading-companies__" : "__cmdk-loading-search__"}><Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />{scope === "companies" ? "Searching companies..." : "Searching..."}</CommandItem>;
    $[35] = companiesLoading;
    $[36] = scope;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== context || $[39] !== push || $[40] !== search || $[41] !== users) {
    t12 = users.length > 0 && <CommandGroup heading="Users">{users.map(user => {
        const title = getCmdkUserTitle(user);
        return <CommandItem key={`user-${user.id}`} keywords={[title, user.email, search]} onSelect={() => {
          context.close();
          push(`/management/users?search=${encodeURIComponent(user.email)}`);
        }} value={`user-${user.id}-${title}-${user.email}`}><UserResultRow user={user} /></CommandItem>;
      })}</CommandGroup>;
    $[38] = context;
    $[39] = push;
    $[40] = search;
    $[41] = users;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== lifecycleItem || $[44] !== t10 || $[45] !== t11 || $[46] !== t12 || $[47] !== t7 || $[48] !== t9) {
    t13 = <>{lifecycleItem}{t7}{t9}{t10}{t11}{t12}</>;
    $[43] = lifecycleItem;
    $[44] = t10;
    $[45] = t11;
    $[46] = t12;
    $[47] = t7;
    $[48] = t9;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  return t13;
};
function _temp(query_0) {
  return query_0.data?.data ?? [];
}
function _temp2(query_1) {
  return query_1.isLoading;
}
function _temp3(query_2) {
  return query_2.isError;
}
function _temp4(group) {
  return group.length === 0;
}
function _temp5(group_0) {
  return group_0.length > 0;
}
function _temp6(drop) {
  return drop.name ?? "";
}